(function () {
  'use strict';

  const ROOT_SELECTOR = '.bls__swiper, [data-hob-carousel-owner]';
  const FRAME_SELECTOR = '[data-hob-carousel-frame]';
  const SLIDE_SELECTOR = '.swiper-slide';
  const ARROW_SELECTOR = '.swiper-arrow';
  const VIEWPORT_IMAGE_SELECTOR = 'img[data-hob-viewport-src]';
  const HOVER_IMAGE_SELECTOR = 'img[data-hob-hover-src]';
  const DEFERRED_PRODUCT_CARD_SELECTOR = '[data-hob-deferred-product-card]';
  const PRODUCT_CARD_SECTION_ID = 'hob-product-card';
  const PRODUCT_TITLE_TAGS = new Set(['div', 'h2', 'h3', 'h4', 'h5', 'h6']);
  const DELEGATED_MEDIA_OWNER_SELECTOR = '[data-hob-assets="hob-product-tabs"]';
  const ARROW_OFFSET_PROPERTY = '--arrows-offset-top';
  const OBSERVER_CLEANUP_REGISTRY_SYMBOL = Symbol.for(
    'house-of-baboon.carousel.observer-cleanup.v1'
  );
  const SWIPER_MUTABLE_SELECTOR = [
    '.swiper-wrapper',
    '.swiper-slide',
    '.swiper-button-next',
    '.swiper-button-prev',
    '.swiper-arrow-next',
    '.swiper-arrow-prev',
    '.swiper-arrow',
    '.swiper-pagination'
  ].join(',');
  function resolveObserverCleanupRegistry() {
    let existing;
    try {
      existing = window[OBSERVER_CLEANUP_REGISTRY_SYMBOL];
    } catch (error) {
      return null;
    }
    try {
      if (
        existing &&
        existing.version === 1 &&
        existing.entries instanceof Set
      ) return existing;
    } catch (error) {
      return null;
    }

    const registry = Object.seal({version: 1, entries: new Set()});
    try {
      Object.defineProperty(window, OBSERVER_CLEANUP_REGISTRY_SYMBOL, {
        value: registry,
        configurable: true,
        writable: false
      });
      return registry;
    } catch (error) {
      try {
        window[OBSERVER_CLEANUP_REGISTRY_SYMBOL] = registry;
        return window[OBSERVER_CLEANUP_REGISTRY_SYMBOL] === registry
          ? registry
          : null;
      } catch (assignmentError) {
        // A hostile non-replaceable preseed disables observers; load/resize remain available.
        return null;
      }
    }
  }

  const observerCleanupRegistry = resolveObserverCleanupRegistry();
  const previousController = window.HobCarousel;

  if (previousController && typeof previousController.destroy === 'function') {
    previousController.destroy(document);
  }

  const state = {
    records: new Map(),
    pendingMounts: new Set(),
    mountObserver: null,
    pendingCardRoots: new Set(),
    cardObserver: null,
    cardHydrations: new Map(),
    cardRequests: new Map(),
    commitQueues: new Map(),
    suspensions: new Set(),
    observerCleanupEntries: observerCleanupRegistry
      ? observerCleanupRegistry.entries
      : new Set(),
    resizeTimer: null,
    listenersAttached: false
  };

  function requestMaintenance(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      return {
        type: 'idle',
        id: window.requestIdleCallback(callback, {timeout: 750})
      };
    }
    return {type: 'timeout', id: window.setTimeout(callback, 50)};
  }

  function cancelMaintenance(handle) {
    if (!handle) return;
    if (handle.type === 'idle' && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(handle.id);
    } else {
      window.clearTimeout(handle.id);
    }
  }

  function isElementStable(element) {
    if (state.suspensions.size) return false;
    const record = state.records.get(element);
    return !record || (
      record.active &&
      !record.interacting &&
      !record.instance?.animating
    );
  }

  function cancelCommitHandle(element) {
    const queue = state.commitQueues.get(element);
    if (!queue?.handle) return;
    cancelMaintenance(queue.handle);
    queue.handle = null;
  }

  function cancelElementCommits(element) {
    const queue = state.commitQueues.get(element);
    if (!queue) return;
    cancelMaintenance(queue.handle);
    queue.jobs.forEach((job) => job.resolve(false));
    state.commitQueues.delete(element);
  }

  function flushElementCommits(element) {
    const queue = state.commitQueues.get(element);
    if (!queue || queue.handle || !queue.jobs.size || !isElementStable(element)) return;

    queue.handle = requestMaintenance(() => {
      queue.handle = null;
      if (!element.isConnected) {
        cancelElementCommits(element);
        return;
      }
      if (!isElementStable(element)) return;

      const jobs = Array.from(queue.jobs.values());
      queue.jobs.clear();
      jobs.forEach((job) => {
        try {
          job.run();
          job.resolve(true);
        } catch (error) {
          job.resolve(false);
        }
      });
      if (!queue.jobs.size) state.commitQueues.delete(element);
    });
  }

  function scheduleElementCommit(element, key, run) {
    if (!element?.isConnected) return Promise.resolve(false);
    let queue = state.commitQueues.get(element);
    if (!queue) {
      queue = {handle: null, jobs: new Map()};
      state.commitQueues.set(element, queue);
    }

    const previous = queue.jobs.get(key);
    if (previous) previous.resolve(false);

    const promise = new Promise((resolve) => {
      queue.jobs.set(key, {run, resolve});
    });
    flushElementCommits(element);
    return promise;
  }

  function flushAllCommits() {
    Array.from(state.commitQueues.keys()).forEach(flushElementCommits);
  }

  function collectRoots(scope) {
    if (!scope) return [];

    const roots = [];
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(ROOT_SELECTOR).forEach((element) => roots.push(element));
    }
    return roots;
  }

  function disconnectMountObserverIfIdle() {
    if (state.pendingMounts.size || !state.mountObserver) return;
    try {
      state.mountObserver.disconnect();
    } catch (error) {
      return;
    }
    state.mountObserver = null;
  }

  function cancelDeferredMount(element) {
    if (!state.pendingMounts.delete(element)) return;
    try {
      state.mountObserver?.unobserve(element);
    } catch (error) {
      // The pending-set fence remains authoritative after a hostile observer.
    }
    disconnectMountObserverIfIdle();
  }

  function ensureMountObserver() {
    if (state.mountObserver || typeof window.IntersectionObserver !== 'function') {
      return state.mountObserver;
    }
    state.mountObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !state.pendingMounts.has(entry.target)) return;
        cancelDeferredMount(entry.target);
        mountElement(entry.target);
      });
    }, {rootMargin: '200px 0px'});
    return state.mountObserver;
  }

  function deferMount(element) {
    if (state.records.has(element) || state.pendingMounts.has(element)) return;
    const observer = ensureMountObserver();
    if (!observer) {
      mountElement(element);
      return;
    }
    state.pendingMounts.add(element);
    try {
      observer.observe(element);
    } catch (error) {
      state.pendingMounts.delete(element);
      mountElement(element);
    }
  }

  function disconnectCardObserverIfIdle() {
    if (state.pendingCardRoots.size || !state.cardObserver) return;
    state.cardObserver.disconnect();
    state.cardObserver = null;
  }

  function cancelDeferredCards(element) {
    if (!state.pendingCardRoots.delete(element)) return;
    try {
      state.cardObserver?.unobserve(element);
    } catch (error) {
      // The pending-set fence remains authoritative after a hostile observer.
    }
    disconnectCardObserverIfIdle();
  }

  function ensureCardObserver() {
    if (state.cardObserver || typeof window.IntersectionObserver !== 'function') {
      return state.cardObserver;
    }
    state.cardObserver = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !state.pendingCardRoots.has(entry.target)) return;
        cancelDeferredCards(entry.target);
        hydrateDeferredProductCards(entry.target);
      });
    }, {rootMargin: '1000px 0px'});
    return state.cardObserver;
  }

  function deferProductCards(element) {
    if (
      !element.querySelector(DEFERRED_PRODUCT_CARD_SELECTOR) ||
      state.pendingCardRoots.has(element) ||
      state.cardHydrations.has(element)
    ) return;

    const observer = ensureCardObserver();
    if (!observer) {
      hydrateDeferredProductCards(element);
      return;
    }
    state.pendingCardRoots.add(element);
    try {
      observer.observe(element);
    } catch (error) {
      state.pendingCardRoots.delete(element);
      hydrateDeferredProductCards(element);
    }
  }

  function productCardRequest(productUrl) {
    let requestUrl;
    try {
      requestUrl = new URL(productUrl, document.baseURI);
    } catch (error) {
      return Promise.reject(error);
    }
    requestUrl.searchParams.set('section_id', PRODUCT_CARD_SECTION_ID);
    requestUrl.hash = '';

    const key = requestUrl.href;
    if (state.cardRequests.has(key)) return state.cardRequests.get(key);

    const request = window.fetch(requestUrl.href, {
      credentials: 'same-origin',
      headers: {'X-Requested-With': 'XMLHttpRequest'}
    }).then((response) => {
      if (!response.ok) throw new Error(`Product card request failed: ${response.status}`);
      return response.text();
    }).catch((error) => {
      state.cardRequests.delete(key);
      throw error;
    });
    state.cardRequests.set(key, request);
    return request;
  }

  function replaceProductTitleTag(card, requestedTag) {
    const tag = String(requestedTag || '').toLowerCase();
    if (!PRODUCT_TITLE_TAGS.has(tag)) return;

    const current = card.querySelector('.bls__product-name');
    if (!current || current.localName === tag) return;

    const replacement = document.createElement(tag);
    Array.from(current.attributes).forEach((attribute) => {
      replacement.setAttribute(attribute.name, attribute.value);
    });
    replacement.append(...current.childNodes);
    current.replaceWith(replacement);
  }

  function parseProductCard(html, requestedTag) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const response = template.content.querySelector('[data-hob-product-card-response]');
    const card = response?.querySelector('.bls__product-item');
    if (!card) throw new Error('Product card response is empty');
    replaceProductTitleTag(card, requestedTag);
    return card;
  }

  function refreshHydratedCarousel(element) {
    const record = state.records.get(element);
    if (!record?.active || !record.instance) return;
    if (typeof record.instance.update === 'function') record.instance.update();
    scheduleCarouselWindow(record);
  }

  function hydrateDeferredProductCards(element) {
    if (typeof window.fetch !== 'function') return Promise.resolve([]);
    const currentJob = state.cardHydrations.get(element);
    if (currentJob) return currentJob.promise;

    const placeholders = Array.from(element.querySelectorAll(DEFERRED_PRODUCT_CARD_SELECTOR));
    if (!placeholders.length) return Promise.resolve([]);

    const job = {promise: null};
    job.promise = Promise.allSettled(placeholders.map((placeholder) => (
      productCardRequest(placeholder.dataset.productUrl).then((html) => ({
        card: parseProductCard(html, placeholder.dataset.productTitleTag),
        placeholder
      }))
    ))).then((results) => {
      if (state.cardHydrations.get(element) !== job) return results;
      return scheduleElementCommit(element, 'product-cards', () => {
        if (state.cardHydrations.get(element) !== job) return;
        results.forEach((result, index) => {
          const placeholder = placeholders[index];
          if (!placeholder?.isConnected || !element.contains(placeholder)) return;
          if (result.status === 'fulfilled') {
            placeholder.replaceWith(result.value.card);
          } else {
            placeholder.removeAttribute('aria-busy');
            placeholder.setAttribute('data-hob-card-error', '');
          }
        });
        refreshHydratedCarousel(element);
      }).then(() => results);
    }).finally(() => {
      if (state.cardHydrations.get(element) === job) state.cardHydrations.delete(element);
    });
    state.cardHydrations.set(element, job);
    return job.promise;
  }

  function captureArrowState(arrow) {
    const value = arrow.style.getPropertyValue(ARROW_OFFSET_PROPERTY);
    return {
      arrow,
      baselineExists: value !== '',
      baselineValue: value,
      baselinePriority: arrow.style.getPropertyPriority(ARROW_OFFSET_PROPERTY),
      hadStyleAttribute: arrow.hasAttribute('style'),
      lastOwnedValue: null,
      lastOwnedPriority: null
    };
  }

  function captureExternalArrowMutation(arrowState) {
    if (arrowState.lastOwnedValue === null) return;

    const currentValue = arrowState.arrow.style.getPropertyValue(ARROW_OFFSET_PROPERTY);
    const currentPriority = arrowState.arrow.style.getPropertyPriority(ARROW_OFFSET_PROPERTY);
    if (
      currentValue === arrowState.lastOwnedValue &&
      currentPriority === arrowState.lastOwnedPriority
    ) return;

    arrowState.baselineExists = currentValue !== '';
    arrowState.baselineValue = currentValue;
    arrowState.baselinePriority = currentPriority;
    arrowState.hadStyleAttribute = arrowState.arrow.hasAttribute('style');
  }

  function applyArrowOffset(record) {
    if (!record.active || state.records.get(record.element) !== record) return;
    if (!record.element.isConnected) {
      disposeRecord(record);
      return;
    }
    if (!record.arrowCenterImage) return;

    let heights;
    if (record.resizeObserverEntry) {
      heights = Array.from(record.imageHeights.values(), (height) => height / 2);
    } else {
      const images = record.element.querySelectorAll('.bls__responsive-image');
      heights = Array.from(images, (image) => image.offsetHeight / 2);
    }
    if (!heights.length) return;

    const offset = `${Math.max(...heights)}px`;
    record.arrowStates.forEach((arrowState) => {
      if (!arrowState.arrow.isConnected || !record.controlRoot.contains(arrowState.arrow)) return;
      captureExternalArrowMutation(arrowState);
      arrowState.arrow.style.setProperty(ARROW_OFFSET_PROPERTY, offset);
      arrowState.lastOwnedValue = arrowState.arrow.style.getPropertyValue(ARROW_OFFSET_PROPERTY);
      arrowState.lastOwnedPriority = arrowState.arrow.style.getPropertyPriority(ARROW_OFFSET_PROPERTY);
    });
  }

  function restoreArrowState(arrowState) {
    const arrow = arrowState.arrow;
    const currentValue = arrow.style.getPropertyValue(ARROW_OFFSET_PROPERTY);
    const currentPriority = arrow.style.getPropertyPriority(ARROW_OFFSET_PROPERTY);
    if (
      arrowState.lastOwnedValue !== null &&
      (
        currentValue !== arrowState.lastOwnedValue ||
        currentPriority !== arrowState.lastOwnedPriority
      )
    ) return;

    if (arrowState.baselineExists) {
      arrow.style.setProperty(
        ARROW_OFFSET_PROPERTY,
        arrowState.baselineValue,
        arrowState.baselinePriority
      );
    } else {
      arrow.style.removeProperty(ARROW_OFFSET_PROPERTY);
      if (!arrowState.hadStyleAttribute && !arrow.getAttribute('style')) {
        arrow.removeAttribute('style');
      }
    }
  }

  function captureMutableShell(element, controlRoot = element) {
    const shellElements = [element, ...element.querySelectorAll(SWIPER_MUTABLE_SELECTOR)];
    if (controlRoot !== element) {
      controlRoot.querySelectorAll(SWIPER_MUTABLE_SELECTOR).forEach((shellElement) => {
        if (!element.contains(shellElement)) shellElements.push(shellElement);
      });
    }
    return shellElements.map((shellElement) => ({
      element: shellElement,
      classAttribute: shellElement.getAttribute('class'),
      styleAttribute: shellElement.getAttribute('style')
    }));
  }

  function restoreAttribute(element, name, value) {
    if (value === null) {
      element.removeAttribute(name);
    } else {
      element.setAttribute(name, value);
    }
  }

  function restoreMutableShell(snapshot) {
    snapshot.forEach((entry) => {
      restoreAttribute(entry.element, 'class', entry.classAttribute);
      restoreAttribute(entry.element, 'style', entry.styleAttribute);
    });
  }

  function disconnectObserverEntry(entry) {
    if (!entry || !entry.observer) {
      state.observerCleanupEntries.delete(entry);
      return true;
    }
    try {
      entry.observer.disconnect();
    } catch (error) {
      state.observerCleanupEntries.add(entry);
      return false;
    }
    entry.observer = null;
    state.observerCleanupEntries.delete(entry);
    return true;
  }

  function disconnectArrowObserver(record) {
    return disconnectObserverEntry(record.resizeObserverEntry);
  }

  function disconnectMediaObserver(record) {
    if (!record.mediaObserver) return;
    record.mediaObserver.disconnect();
    record.mediaObserver = null;
  }

  function hydrateDeferredImage(image) {
    if (!image) return false;
    let src = image.dataset.hobViewportSrc;
    let srcset = image.dataset.hobViewportSrcset;
    let sourceKind = 'viewport';
    if (!src) {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return false;
      src = image.dataset.hobHoverSrc;
      srcset = image.dataset.hobHoverSrcset;
      sourceKind = 'hover';
    }
    if (!src) return false;

    if (srcset) image.srcset = srcset;
    image.src = src;
    image.removeAttribute(`data-hob-${sourceKind}-srcset`);
    image.removeAttribute(`data-hob-${sourceKind}-src`);
    image.classList.add('bls-loaded-image');
    image.closest('.bls-image-js')?.classList.remove('bls-loading-image');
    return true;
  }

  function hydrateCarouselWindow(record) {
    if (!record.active || !record.instance || !record.mediaReady) return;
    const slides = Array.from(record.instance.slides || []);
    if (!slides.length) return;
    const configuredView = record.instance.params?.slidesPerView;
    const visibleCount = configuredView === 'auto'
      ? Math.ceil(record.instance.slidesPerViewDynamic?.() || 1)
      : Math.ceil(Number(configuredView) || 1);
    const start = Math.max(0, Number(record.instance.activeIndex || 0) - 1);
    const end = Math.min(slides.length, start + visibleCount + 2);
    slides.slice(start, end).forEach((slide) => {
      slide.querySelectorAll(`${VIEWPORT_IMAGE_SELECTOR}, ${HOVER_IMAGE_SELECTOR}`).forEach(hydrateDeferredImage);
    });
  }

  function scheduleCarouselWindow(record) {
    if (!record?.active || !record.element || !record.mediaReady) return;
    scheduleElementCommit(record.element, 'carousel-media', () => {
      if (!record.active || state.records.get(record.element) !== record) return;
      hydrateCarouselWindow(record);
    });
  }

  function installMediaObserver(record) {
    if (record.element.closest(DELEGATED_MEDIA_OWNER_SELECTOR)) return;
    const deferredImages = record.element.querySelectorAll(
      `${VIEWPORT_IMAGE_SELECTOR}, ${HOVER_IMAGE_SELECTOR}`
    );
    if (!deferredImages.length) return;

    if (typeof window.IntersectionObserver !== 'function') {
      record.mediaReady = true;
      scheduleCarouselWindow(record);
      return;
    }

    record.mediaObserver = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      record.mediaReady = true;
      scheduleCarouselWindow(record);
      disconnectMediaObserver(record);
    }, {rootMargin: '600px'});
    record.mediaObserver.observe(record.element);
  }

  function retryObserverCleanup(scope = document) {
    const retryAll = scope === document;
    Array.from(state.observerCleanupEntries).forEach((entry) => {
      if (
        !entry ||
        !entry.element ||
        !entry.observer ||
        typeof entry.observer.disconnect !== 'function'
      ) {
        state.observerCleanupEntries.delete(entry);
        return;
      }
      if (
        retryAll ||
        entry.element === scope ||
        (typeof scope.contains === 'function' && scope.contains(entry.element))
      ) {
        disconnectObserverEntry(entry);
      }
    });
  }

  function hasPendingObserverCleanup(element) {
    return Array.from(state.observerCleanupEntries).some((entry) => (
      entry && entry.element === element && entry.observer
    ));
  }

  function installArrowObserver(record) {
    if (
      !observerCleanupRegistry ||
      !record.arrowCenterImage ||
      typeof window.ResizeObserver !== 'function'
    ) return true;

    const images = record.element.querySelectorAll('.bls__responsive-image');
    if (!images.length) return true;

    try {
      const observer = new window.ResizeObserver(function (entries) {
        if (!record.active || state.records.get(record.element) !== record) return;
        if (!record.element.isConnected) {
          disposeRecord(record);
          return;
        }
        entries.forEach((entry) => {
          const borderBox = Array.isArray(entry.borderBoxSize)
            ? entry.borderBoxSize[0]
            : entry.borderBoxSize;
          const height = borderBox?.blockSize ?? entry.contentRect?.height;
          if (Number.isFinite(height)) record.imageHeights.set(entry.target, height);
        });
        applyArrowOffset(record);
      });
      record.resizeObserverEntry = {element: record.element, observer};
      images.forEach((image) => observer.observe(image));
      return true;
    } catch (error) {
      disposeRecord(record, true);
      return false;
    }
  }

  function destroyOwnedInstance(record) {
    const attachedInstance = record.element.swiper;
    const instance = record.instance || (
      attachedInstance && attachedInstance !== record.foreignInstance
        ? attachedInstance
        : null
    );

    if (instance && typeof instance.destroy === 'function') {
      try {
        instance.destroy(true, true);
      } catch (error) {
        // Cleanup below remains authoritative even when a partial constructor fails.
      }
    }

    if (record.element.swiper === instance) {
      try {
        delete record.element.swiper;
      } catch (error) {
        // Fall through to the explicit detach below.
      }
      if (record.element.swiper === instance) {
        try {
          record.element.swiper = null;
        } catch (error) {
          // Swiper can expose a non-writable element property in test adapters.
        }
      }
    }
  }

  function disposeRecord(record, restoreConstructionState = false) {
    if (!record || !record.active) return;

    cancelElementCommits(record.element);
    record.active = false;
    if (state.records.get(record.element) === record) {
      state.records.delete(record.element);
    }
    disconnectArrowObserver(record);
    disconnectMediaObserver(record);
    destroyOwnedInstance(record);
    record.arrowStates.forEach(restoreArrowState);
    if (restoreConstructionState && record.constructionSnapshot) {
      restoreMutableShell(record.constructionSnapshot);
    }
    record.constructionSnapshot = null;
    record.instance = null;
    record.foreignInstance = null;
    record.arrowStates = [];
    record.imageHeights.clear();
    record.resizeObserverEntry = null;
    record.mediaObserver = null;
    record.mediaReady = false;
    record.interacting = false;
    record.resumeAutoplay = false;
    record.controlRoot = null;
    record.element = null;
  }

  function resolveCarouselPart(element, controlRoot, selector) {
    return element.querySelector(selector) || (
      controlRoot !== element ? controlRoot.querySelector(selector) : null
    );
  }

  function createOptions(element, record) {
    let autoplay = element.dataset.autoplay === 'true';
    const loop = element.dataset.loop === 'true';
    const slideshow = element.dataset.slideshow ? element.dataset.slideshow : 0;
    const banner = element.dataset.banner ? element.dataset.banner : 0;
    const imageIcon = element.dataset.imageIcon ? element.dataset.imageIcon : 0;
    const collection = element.dataset.collection ? element.dataset.collection : 0;
    const itemDesktop = element.dataset.desktop ? element.dataset.desktop : 4;
    const itemTablet = element.dataset.tablet ? element.dataset.tablet : 2;
    const itemMobile = element.dataset.mobile ? element.dataset.mobile : 1;
    const itemSmallMobile = element.dataset.smallMobile ? element.dataset.smallMobile : 1;
    let autoplaySpeed = element.dataset.autoplaySpeed ? element.dataset.autoplaySpeed : 3000;
    let speed = element.dataset.speed ? element.dataset.speed : 400;
    const effect = element.dataset.effect ? element.dataset.effect : 'slide';
    const row = element.dataset.row ? element.dataset.row : 1;
    let spacing = element.dataset.spacing ? element.dataset.spacing : 0;
    const progressbar = element.dataset.paginationProgressbar === 'true';
    const autoItem = element.dataset.itemMobile === 'true';

    spacing = Number(spacing);
    autoplaySpeed = Number(autoplaySpeed);
    speed = Number(speed);
    if (autoplay) autoplay = {delay: autoplaySpeed};

    record.slideshow = slideshow;
    record.banner = banner;

    return {
      slidesPerView: autoItem ? 'auto' : collection ? itemSmallMobile : itemMobile,
      spaceBetween: spacing >= 15 ? 15 : spacing,
      autoplay,
      loop,
      effect,
      speed,
      watchSlidesProgress: false,
      lazyPreloadPrevNext: 2,
      grid: {
        rows: row,
        fill: 'row'
      },
      navigation: {
        nextEl: banner
          ? resolveCarouselPart(element, record.controlRoot, '.swiper-arrow-next')
          : resolveCarouselPart(element, record.controlRoot, '.swiper-button-next'),
        prevEl: banner
          ? resolveCarouselPart(element, record.controlRoot, '.swiper-arrow-prev')
          : resolveCarouselPart(element, record.controlRoot, '.swiper-button-prev')
      },
      pagination: {
        clickable: true,
        el: resolveCarouselPart(element, record.controlRoot, '.swiper-pagination'),
        type: progressbar ? 'progressbar' : 'bullets'
      },
      breakpoints: {
        576: {
          slidesPerView: imageIcon ? itemTablet : itemMobile
        },
        768: {
          slidesPerView: itemTablet,
          spaceBetween: spacing >= 30 ? 30 : spacing
        },
        1200: {
          slidesPerView: itemDesktop,
          spaceBetween: spacing
        }
      },
      on: {
        init: function () {
          if (!record.resizeObserverEntry) applyArrowOffset(record);
        }
      }
    };
  }

  function attachBehavior(record) {
    if (!record.instance || typeof record.instance.on !== 'function') return;

    const startInteraction = function () {
      if (!record.active) return;
      record.interacting = true;
      cancelCommitHandle(record.element);
    };
    const finishInteraction = function () {
      if (!record.active) return;
      record.interacting = false;
      flushElementCommits(record.element);
    };

    record.instance.on('touchStart', startInteraction);
    record.instance.on('sliderFirstMove', startInteraction);
    record.instance.on('touchEnd', finishInteraction);
    record.instance.on('transitionEnd', finishInteraction);

    record.instance.on('slideChange', function () {
      record.mediaReady = true;
      scheduleCarouselWindow(record);
    });

    if (record.slideshow) {
      record.instance.on('slideChange', function () {
        document.querySelectorAll('.video-slider').forEach((video) => {
          const dataVideo = video.dataset.video;
          const dataPoster = video.dataset.poster;
          video.innerHTML = `
              <video playsinline="true" loop="loop" muted="muted" autoplay="autoplay" preload="metadata"
                poster="${dataPoster}">
                <source
                  src="${dataVideo}"
                  type="video/mp4">
              </video>
              `;
        });
      });
    }

    if (record.banner) {
      record.instance.on('slideChange', function () {
        BlsLazyloadImg.init();
      });
    }
  }

  function mountElement(element) {
    if (!element || element.nodeType !== Node.ELEMENT_NODE) return null;
    cancelDeferredMount(element);
    cancelDeferredCards(element);
    retryObserverCleanup(element);
    if (hasPendingObserverCleanup(element)) return null;
    installOwnerListeners();
    if (!element.matches(ROOT_SELECTOR) || !element.isConnected) return null;
    hydrateDeferredProductCards(element);

    const currentRecord = state.records.get(element);
    if (currentRecord && currentRecord.active) return currentRecord.instance;
    if (element.swiper || element.classList.contains('swiper-initialized')) return null;
    if (!element.querySelectorAll(SLIDE_SELECTOR).length) return null;
    if (typeof window.Swiper !== 'function') return null;

    const controlRoot = element.closest(FRAME_SELECTOR) || element;
    const record = {
      active: true,
      element,
      controlRoot,
      instance: null,
      foreignInstance: element.swiper || null,
      arrowCenterImage: element.dataset.arrowCenterimage
        ? element.dataset.arrowCenterimage
        : 0,
      arrowStates: Array.from(controlRoot.querySelectorAll(ARROW_SELECTOR), captureArrowState),
      imageHeights: new Map(),
      constructionSnapshot: captureMutableShell(element, controlRoot),
      resizeObserverEntry: null,
      mediaObserver: null,
      mediaReady: false,
      interacting: false,
      resumeAutoplay: false,
      slideshow: 0,
      banner: 0
    };
    state.records.set(element, record);
    if (!installArrowObserver(record)) return null;

    try {
      record.instance = new Swiper(element, createOptions(element, record));
      attachBehavior(record);
      installMediaObserver(record);
      if (state.suspensions.size) pauseRecord(record);
      record.constructionSnapshot = null;
      return record.instance;
    } catch (error) {
      disposeRecord(record, true);
      return null;
    }
  }

  function isDeferredByComponentOwner(element) {
    const componentOwner = element.closest(DELEGATED_MEDIA_OWNER_SELECTOR);
    if (!componentOwner) return false;
    const panel = element.closest('.bls__cls-tab');
    return Boolean(panel && componentOwner.contains(panel) && !panel.classList.contains('active'));
  }

  function mount(scope = document) {
    retryObserverCleanup(scope);
    installOwnerListeners();
    collectRoots(scope).forEach((element) => {
      if (!isDeferredByComponentOwner(element)) {
        deferProductCards(element);
        deferMount(element);
      }
    });
  }

  function clearResizeTimer() {
    if (state.resizeTimer === null) return;
    window.clearTimeout(state.resizeTimer);
    state.resizeTimer = null;
  }

  function pauseRecord(record) {
    const autoplay = record?.instance?.autoplay;
    if (!autoplay || typeof autoplay.pause !== 'function') return;
    if (autoplay.running && !autoplay.paused) {
      record.resumeAutoplay = true;
      autoplay.pause();
    }
  }

  function resumeRecord(record) {
    if (!record?.resumeAutoplay) return;
    record.resumeAutoplay = false;
    const autoplay = record.instance?.autoplay;
    if (autoplay?.running && typeof autoplay.resume === 'function') autoplay.resume();
  }

  function suspend(reason = 'external') {
    const key = String(reason || 'external');
    const wasActive = state.suspensions.size > 0;
    state.suspensions.add(key);
    if (wasActive) return;
    state.records.forEach((record) => {
      cancelCommitHandle(record.element);
      pauseRecord(record);
    });
  }

  function resume(reason = 'external') {
    state.suspensions.delete(String(reason || 'external'));
    if (state.suspensions.size) return;
    state.records.forEach(resumeRecord);
    flushAllCommits();
  }

  function removeOwnerListeners() {
    if (!state.listenersAttached) return;
    document.removeEventListener('shopify:section:load', handleSectionLoad, false);
    document.removeEventListener('shopify:section:unload', handleSectionUnload, false);
    window.removeEventListener('resize', handleResize, true);
    window.removeEventListener('load', handleWindowLoad, false);
    state.listenersAttached = false;
  }

  function installOwnerListeners() {
    if (state.listenersAttached) return;
    document.addEventListener('shopify:section:load', handleSectionLoad, false);
    document.addEventListener('shopify:section:unload', handleSectionUnload, false);
    window.addEventListener('resize', handleResize, true);
    window.addEventListener('load', handleWindowLoad, false);
    state.listenersAttached = true;
  }

  function destroy(scope = document) {
    if (!scope) return;

    const destroyAll = scope === document;
    Array.from(state.pendingMounts).forEach((element) => {
      if (
        destroyAll ||
        element === scope ||
        (typeof scope.contains === 'function' && scope.contains(element))
      ) cancelDeferredMount(element);
    });
    Array.from(state.pendingCardRoots).forEach((element) => {
      if (
        destroyAll ||
        element === scope ||
        (typeof scope.contains === 'function' && scope.contains(element))
      ) cancelDeferredCards(element);
    });
    Array.from(state.cardHydrations.keys()).forEach((element) => {
      if (
        destroyAll ||
        element === scope ||
        (typeof scope.contains === 'function' && scope.contains(element))
      ) state.cardHydrations.delete(element);
    });
    Array.from(state.commitQueues.keys()).forEach((element) => {
      if (
        destroyAll ||
        element === scope ||
        (typeof scope.contains === 'function' && scope.contains(element))
      ) cancelElementCommits(element);
    });
    retryObserverCleanup(scope);
    Array.from(state.records.values()).forEach((record) => {
      if (
        destroyAll ||
        record.element === scope ||
        (typeof scope.contains === 'function' && scope.contains(record.element))
      ) {
        disposeRecord(record);
      }
    });

    if (destroyAll) retryObserverCleanup(scope);
    if (destroyAll || state.records.size === 0) clearResizeTimer();

    if (destroyAll) {
      disconnectMountObserverIfIdle();
      disconnectCardObserverIfIdle();
      state.cardRequests.clear();
      state.suspensions.clear();
      removeOwnerListeners();
    }
  }

  function refreshOwnedArrows() {
    Array.from(state.records.values()).forEach(applyArrowOffset);
  }

  function handleResize() {
    clearResizeTimer();
    state.resizeTimer = window.setTimeout(function () {
      state.resizeTimer = null;
      refreshOwnedArrows();
    }, 100);
  }

  function handleWindowLoad() {
    refreshOwnedArrows();
  }

  function handleSectionLoad(event) {
    mount(event.target);
  }

  function handleSectionUnload(event) {
    destroy(event.target);
  }

  window.HobCarousel = Object.freeze({mount, mountElement, destroy, suspend, resume});
  mount(document);
})();
